import { Printer, RotateCcw, Lock, Copy, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BackButton, PageHeader, StatusBadge } from '@/components/atoms'
import { copyText } from '@/utils/copyText'
import { useToast } from '@/hooks/ui'
import { useState } from 'react'

interface OrderDetailHeaderProps {
    orderRef: string
    createdAt: string
    status: string
    onBack: () => void
    onPrint?: () => void
    isEditable?: boolean
}

export const OrderDetailHeader = ({
    orderRef,
    createdAt,
    status,
    onBack,
    onPrint,
    isEditable = true,
}: OrderDetailHeaderProps) => {
    const { success, error } = useToast()
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
        try {
            await copyText(orderRef)
            setCopied(true)
            success(`Order ${orderRef} copied to clipboard.`)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            error('Could not copy reference to clipboard.')
        }
    }

    return (
        <PageHeader
            size="large"
            back={<BackButton onClick={onBack} label="Back to orders" />}
            title={
                <button
                    type="button"
                    className="group inline-flex max-w-full items-center gap-2 text-left"
                    onClick={handleCopy}
                    title="Copy order reference"
                >
                    <span className="truncate">{orderRef}</span>
                    {copied ? (
                        <Check className="size-4 shrink-0 text-success-dark" />
                    ) : (
                        <Copy className="size-4 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" />
                    )}
                </button>
            }
            badge={
                <>
                    <StatusBadge type="order" status={status} />
                    {!isEditable && (
                        <Badge tone="warning">
                            <Lock className="size-3" />
                            Read-only
                        </Badge>
                    )}
                </>
            }
            description={new Date(createdAt).toLocaleString()}
            actions={
                <>
                    {status === 'delivered' && (
                        <Button variant="secondary">
                            <RotateCcw />
                            Return
                        </Button>
                    )}
                    <Button variant="secondary" onClick={onPrint}>
                        <Printer />
                        Print
                    </Button>
                </>
            }
        />
    )
}
